import { AlertV2, Checkbox } from '@cimplico/components';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useMapWorkpaperAccounts } from '../../queries/use-map-workpaper-accounts';
import { Account } from '../../types/account';
import { COAProvider, Entity, EntityAccount } from '../../types/entity';
import AccountsMapResult from './AccountsMapResult';
import LinkingBanner from './LinkingBanner';
import { Workpaper } from '../../types/workpaper';
import AccountMapRow from './AccountMapRow';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

// Estimated height of each account mapping row
const ITEM_HEIGHT = 56;

/**
 * Converts an array of EntityAccount objects into a Record/map for quick lookups.
 *
 * @param entityAccounts - Array of EntityAccount objects to be converted into a map
 * @returns Record object with EntityAccount IDs as keys and EntityAccount objects as values
 */
function toMap(entityAccounts: EntityAccount[]): Record<string, EntityAccount> {
    return entityAccounts.reduce((acc, ea) => {
        acc[ea.id] = ea;
        return acc;
    }, {} as Record<string, EntityAccount>);
}

export type AccountMapping = {
    account: Account;
    // This is set when a user selects an entity account. This can be modified before confirmation
    entityAccount?: EntityAccount;
    // This indicates that workpaper has a pre-established mapping so it cannot be changed
    originalEntityAccount?: EntityAccount;
    index: number;
};
export type MappingFormData = {
    mappings: AccountMapping[];
};

type AccountsMappingFormProps = {
    workpaper: Workpaper;
    entity: Entity;
    accounts: Account[];
    entityAccounts: EntityAccount[];
    chartProvider?: COAProvider | null;
    isLoading?: boolean;
    onMap: (data: { workpaperId: string; accountsConfirmed: number }) => void;
};

function AccountsMappingForm({
    workpaper,
    entity,
    accounts,
    entityAccounts,
    isLoading = false,
    chartProvider,
    onMap,
}: AccountsMappingFormProps): JSX.Element {
    const mapAccountsQuery = useMapWorkpaperAccounts(workpaper.id);
    const bannerRef = useRef<HTMLDivElement>(null);
    const bannerInView = useInView(bannerRef);
    const entityAccountsMap = useMemo(() => toMap(entityAccounts), [entityAccounts]);
    const [hideMapped, setHideMapped] = useState(false);

    const form = useForm<MappingFormData>({
        defaultValues: {
            mappings: accounts.map((acc, index) => ({
                account: acc,
                originalEntityAccount: acc.entityAccountId ? entityAccountsMap[acc.entityAccountId] : undefined,
                entityAccount: acc.entityAccountId ? entityAccountsMap[acc.entityAccountId] : undefined,
                index,
            })),
        },
    });

    useEffect(() => {
        form.reset({
            mappings: accounts.map((acc, index) => ({
                account: acc,
                originalEntityAccount: acc.entityAccountId ? entityAccountsMap[acc.entityAccountId] : undefined,
                entityAccount: acc.entityAccountId ? entityAccountsMap[acc.entityAccountId] : undefined,
                index,
            })),
        });
    }, [accounts, entityAccountsMap, form]);

    function onSubmit(data: MappingFormData): void {
        const dirtyAccounts = data.mappings
            .filter((mapping, idx) => {
                const dirtyField = form.formState.dirtyFields.mappings?.at(idx);
                return Boolean(dirtyField);
            })
            .map((da) => {
                return {
                    accountId: da.account.id,
                    entityAccountId: da.entityAccount?.id,
                };
            });

        mapAccountsQuery.mutate(dirtyAccounts, {
            onSuccess: (res) => {
                const successfulMappings = res.filter((result) => result.outcome === 'success').length;
                if (successfulMappings > 0) {
                    onMap({ workpaperId: workpaper.id, accountsConfirmed: successfulMappings });
                }
            },
        });
    }

    const mappings = useWatch({ control: form.control, name: 'mappings' });
    const filteredMappings = useMemo(
        () => mappings.filter((acc) => (hideMapped ? !acc.originalEntityAccount : true)),
        [mappings, hideMapped]
    );

    // Entity account ids that have been used on a mapping and are no longer available for selection.
    const usedMappings = useMemo(() => {
        return new Set(mappings.filter((value) => value.entityAccount).map((v) => v.entityAccount!.id));
    }, [mappings]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={(e): void => {
                    // Stop propagation is necessary as this form may be embedded in other forms
                    e.stopPropagation();
                    form.handleSubmit(onSubmit)(e);
                }}
                className="flex flex-col gap-2 max-w-[1016px]"
            >
                {(mapAccountsQuery.data || mapAccountsQuery.error) && (
                    <AccountsMapResult
                        status={mapAccountsQuery.error ? 'error' : 'success'}
                        results={(mapAccountsQuery.data || mapAccountsQuery.error.response?.data) ?? []}
                    />
                )}
                <div ref={bannerRef}>
                    <LinkingBanner
                        entity={entity}
                        chartProvider={chartProvider}
                        isLoading={isLoading}
                        isSaving={mapAccountsQuery.isLoading}
                    />
                </div>
                <Checkbox label="Hide mapped accounts" checked={hideMapped} onChange={(e): void => setHideMapped(e.target.checked)} />
                <div className="relative w-fit h-fit">
                    <div className="grid grid-cols-[400px_100px_450px] gap-x-8 border rounded-md w-fit">
                        <div className="grid grid-cols-[subgrid] col-span-full border-b py-2 text-sm font-medium text-gray-800 bg-gray-100 rounded-t px-4">
                            <p className="col-span-2">Account in workpaper</p>
                            <p>Account in chart</p>
                        </div>
                        {isLoading && <LoadingIndicator />}
                        {!isLoading && (
                            <div
                                className="grid grid-cols-[subgrid] col-span-full"
                                style={{ height: Math.min(700, ITEM_HEIGHT * filteredMappings.length) }}
                            >
                                {/* Virtualized list to handle a large amount of accounts */}
                                <AutoSizer>
                                    {/* @ts-ignore */}
                                    {({ height, width }): JSX.Element => {
                                        return (
                                            <FixedSizeList
                                                height={height}
                                                width={width}
                                                itemCount={filteredMappings.length}
                                                itemSize={ITEM_HEIGHT}
                                                itemData={filteredMappings}
                                            >
                                                {({ data, index, style }): JSX.Element => {
                                                    const item = data[index];
                                                    return (
                                                        <AccountMapRow
                                                            entityAccounts={entityAccounts}
                                                            mapping={item}
                                                            idx={item.index}
                                                            usedMappings={usedMappings}
                                                            style={style}
                                                            key={item.account.id}
                                                        />
                                                    );
                                                }}
                                            </FixedSizeList>
                                        );
                                    }}
                                </AutoSizer>
                            </div>
                        )}
                        {hideMapped && mappings.filter((f) => !f.entityAccount).length === 0 && (
                            <div className="py-24 flex items-center justify-center flex-col col-span-full">
                                <div className="p-3 border border-gray-300 rounded-2xl -rotate-[75deg]">
                                    <div className="p-1 border border-gray-300 rounded-md rotate-[75deg]">
                                        <BuildingLibraryIcon className="h-8 w-8 text-gray-800" />
                                    </div>
                                </div>
                                <p className="mt-4 text-gray-800 font-bold">No accounts found</p>
                                <p className="text-sm text-gray-600">There are no remaining accounts to map</p>
                            </div>
                        )}
                    </div>

                    <AnimatePresence>
                        {!bannerInView && (
                            <motion.div
                                key="floating-banner"
                                className="bg-white sticky mt-2 shadow-lg -translate-x-[2.5%] w-[105%] mx-auto"
                                initial={{ opacity: 0, bottom: -20 }}
                                exit={{ opacity: 0, bottom: -20 }}
                                animate={{ opacity: 1, bottom: 16 }}
                                transition={{ duration: 0.3 }}
                            >
                                <LinkingBanner entity={entity} chartProvider={chartProvider} isLoading={isLoading} bannerRef={bannerRef} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </form>
        </FormProvider>
    );
}

function LoadingIndicator(): JSX.Element {
    return (
        <>
            {[...Array(50).keys()]
                .map((i) => i + 1)
                .map((i) => {
                    return (
                        <Fragment key={i}>
                            <div className="col-span-2 py-2 px-4 h-[50px] w-full">
                                <div className="bg-gray-200 animate-pulse h-full w-full rounded"></div>
                            </div>
                            <div className=" h-[50px] py-2 px-4 w-full">
                                <div className="bg-gray-200 animate-pulse  h-full w-full rounded"></div>
                            </div>
                        </Fragment>
                    );
                })}
        </>
    );
}

export default AccountsMappingForm;
